import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "system/store";
import { policyEditorActions } from "system/store/slices/policy-editor-slice";
import { IniInfo, UnconfiguredRules } from "system/types/interfaces";
import { usePolicyEditorPermissions } from "system/hooks";
import { ConfiguredTable } from "../../subjectViewPage/configurationTab/ConfiguredTable";
import { NotConfiguredTable } from "../../subjectViewPage/configurationTab/NotConfiguredTable";
import { AddConfigRuleModal } from "../../subjectViewPage/configurationTab/AddConfigRuleModal";
import { ConfigRuleApplicabilityModal } from "../../subjectViewPage/configurationTab/ConfigRuleApplicabilityModal";
import { defaultNotConfiguredRules } from "./defaultNotConfiguredRules";
import { normalizeAuthoredConfigValue } from "components/modules/policy-editor/utils/configValueQuoting";
import { toggleCommentRow } from "components/modules/policy-editor/utils/configCommentRows";

export const ClientConfigTab: React.FC = () => {
  const dispatch = useDispatch();
  const configuredRules = useSelector((state: RootState) => state.policyEditor.clientConfig || []);
  const { isReadOnly: readOnlyViewer } = usePolicyEditorPermissions();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [insertIndex, setInsertIndex] = useState<number | null>(null);
  const [showApplicabilityModal, setShowApplicabilityModal] = useState(false);
  const [draftConfigRule, setDraftConfigRule] = useState<IniInfo | null>(null);
  const [selectedUnconfigured, setSelectedUnconfigured] = useState<UnconfiguredRules | null>(null);

  const checkForDuplicateRule = (newRule: IniInfo, currentId: string | null): boolean => {
    const newRuleString = `${newRule.name} ${newRule.delimiter} ${newRule.applicability} ${newRule.value}`.trim();
    return configuredRules.some((existingRule) => {
      if (currentId && existingRule.id === currentId) return false;
      if (existingRule.isDeleted) return false;
      const existingRuleString =
        `${existingRule.name} ${existingRule.delimiter} ${existingRule.applicability} ${existingRule.value}`.trim();
      return existingRuleString === newRuleString;
    });
  };

  const handleAddRule = (newRule: {
    rule: string;
    applicability: string;
    value: string;
    description: string;
    type: string;
    regex?: string;
  }) => {
    const draft: IniInfo = {
      id: Date.now().toString(),
      name: newRule.rule,
      value: newRule.value,
      applicability: "endpoint",
      delimiter: "is for any",
      pendingChanges: true,
      description: newRule.description,
      type: newRule.type,
      regex: newRule.regex,
    };

    setDraftConfigRule(draft);
    setIsAddModalOpen(false);
    setSelectedUnconfigured(null);
    setShowApplicabilityModal(true);
  };

  const handleApplicabilityConfirmed = (finalRule: IniInfo) => {
    finalRule.value =
      normalizeAuthoredConfigValue(finalRule.name ?? "", finalRule.value, finalRule.delimiter ?? "", finalRule.applicability ?? "") ??
      finalRule.value;

    if (checkForDuplicateRule(finalRule, finalRule.id ?? "")) {
      // setErrorMessage(`The rule "${finalRule.name}" with value "${finalRule.value}" already exists.`);
      return `The rule "${finalRule.name}" with value "${finalRule.value}" and applicability "${finalRule.delimiter} ${finalRule.applicability}" already exists.`;
    }

    const existingIndex = configuredRules.findIndex((x) => x.id === finalRule.id);

    if (existingIndex >= 0) {
      dispatch(policyEditorActions.updateClientRule({ item: finalRule }));
    } else {
      const insertPosition = insertIndex ?? configuredRules.length;
      dispatch(policyEditorActions.addClientRule({ item: finalRule, position: insertPosition }));
    }

    setInsertIndex(null);
    setShowApplicabilityModal(false);
    setDraftConfigRule(null);
  };

  const handleToggleComment = (id: string, isComment: boolean) => {
    const rule = configuredRules.find((r) => r.id === id);
    if (!rule) return;

    const toggled = toggleCommentRow(rule, isComment);
    if (toggled) dispatch(policyEditorActions.updateClientRule({ item: toggled }));
  };

  const handleDelete = (id: string) => {
    dispatch(policyEditorActions.deleteClientRule({ id }));
  };

  const handleUndoDelete = (id: string) => {
    dispatch(policyEditorActions.undoDeleteClientRule({ id }));
  };

  const handleReorder = (oldIndex: number, newIndex: number) => {
    dispatch(policyEditorActions.reorderClientRules({ oldIndex, newIndex }));
  };

  const handleOpenEditModal = (rule: IniInfo) => {
    setDraftConfigRule(rule);
    setShowApplicabilityModal(true);
  };

  return (
    <Box>
      <Typography variant="h6" mb={2} sx={{ color: "#fff" }}>
        Configured Rules
      </Typography>
      <ConfiguredTable
        configuredRules={configuredRules}
        onAdd={(index: number) => {
          setInsertIndex(index);
          setIsAddModalOpen(true);
        }}
        onDelete={handleDelete}
        onUndoDelete={handleUndoDelete}
        onEdit={handleOpenEditModal}
        onToggleComment={handleToggleComment}
        onReorder={handleReorder}
        readOnlyViewer={readOnlyViewer}
      />

      <Typography variant="h6" sx={{ color: "#fff", mt: 3, mb: 1 }}>
        Default Configurations
      </Typography>
      <NotConfiguredTable
        notConfiguredRules={defaultNotConfiguredRules}
        readOnlyViewer={readOnlyViewer}
        onAdd={(ruleName) => {
          const found = defaultNotConfiguredRules.find((x) => x.name === ruleName);
          if (!found) return;

          setSelectedUnconfigured(found);
          setIsAddModalOpen(true);
        }}
      />
      <AddConfigRuleModal
        open={isAddModalOpen}
        onClose={() => {
          setSelectedUnconfigured(null);
          setIsAddModalOpen(false);
        }}
        onAddRule={handleAddRule}
        unConfiguredList={defaultNotConfiguredRules}
        initialItem={selectedUnconfigured}
        showCommands={false} // Disable commands for ClientConfigTab
      />
      {draftConfigRule && (
        <ConfigRuleApplicabilityModal
          open={showApplicabilityModal}
          onClose={() => {
            setShowApplicabilityModal(false);
            setDraftConfigRule(null);
          }}
          initialRule={draftConfigRule}
          onFinish={(finalRule) => {
            return handleApplicabilityConfirmed(finalRule);
          }}
        />
      )}
    </Box>
  );
};
